(() => {
  if (window.MilroysTastings) return;
  const TOKEN_INDEX = 'milroys-event-booking-ids';
  const MOCK_BOOKINGS_KEY = 'milroys-tastings-mock-bookings';
  const CHECKOUT_EVENT_KEY = 'milroys-tasting-checkout-event';
  let routeConfig = {};
  const pinIcon =
    '<svg viewBox="0 0 24 24"><path d="M12 21s7-5.1 7-12a7 7 0 1 0-14 0c0 6.9 7 12 7 12Z"/><circle cx="12" cy="9" r="2.2"/></svg>';
  const clockIcon =
    '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>';

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  function money(value, currency = 'GBP') {
    const amount = Number(value || 0);
    if (amount === 0) return 'Free';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);
  }

  function isEcaRsvp(event) {
    return event?.booking_mode === 'eca_rsvp';
  }

  function isNotice(event) {
    return event?.booking_mode === 'notice';
  }

  function isDiaryNote(event) {
    if (!isNotice(event)) return false;
    const label = String(event.notice_label || '');
    return label === 'Closed' || label === 'To be announced';
  }

  function whenRange(event) {
    if (event?.all_day) return 'All day';
    const bits = dateBits(event.starts_at);
    if (!event?.ends_at) return bits.time;
    const endBits = dateBits(event.ends_at);
    const sameDay = bits.day === endBits.day && bits.month === endBits.month;
    return sameDay ? `${bits.time} – ${endBits.time}` : `${bits.time} – ${endBits.weekdayShort} ${endBits.day} ${endBits.time}`;
  }

  function londonDayKey(isoOrDate) {
    const date = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
    if (Number.isNaN(date.getTime())) return '';
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;
    return year && month && day ? `${year}-${month}-${day}` : '';
  }

  function monthKey(iso) {
    const day = londonDayKey(iso);
    return day ? day.slice(0, 7) : '';
  }

  // Same rule as apps/tastings-3gs/src/map.mjs so lists stay current between catalogue syncs.
  function isCurrentEvent(event, now = new Date()) {
    const start = new Date(event?.starts_at || '');
    if (Number.isNaN(start.getTime())) return false;
    const end = event?.ends_at ? new Date(event.ends_at) : null;
    if (end && !Number.isNaN(end.getTime()) && end.getTime() >= start.getTime()) {
      return now.getTime() <= end.getTime();
    }
    const startDay = londonDayKey(start);
    const today = londonDayKey(now);
    return Boolean(startDay && today && startDay >= today);
  }

  function monthLabel(key, style = 'long') {
    const [year, month] = String(key || '').split('-');
    if (!year || !month) return 'Any month';
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, 15));
    return new Intl.DateTimeFormat('en-GB', { month: style, year: 'numeric', timeZone: 'UTC' }).format(date);
  }

  function dateBits(iso) {
    const date = new Date(iso);
    const zone = { timeZone: 'Europe/London' };
    return {
      month: new Intl.DateTimeFormat('en-GB', { month: 'long', ...zone }).format(date),
      monthShort: new Intl.DateTimeFormat('en-GB', { month: 'short', ...zone }).format(date),
      day: new Intl.DateTimeFormat('en-GB', { day: 'numeric', ...zone }).format(date),
      weekday: new Intl.DateTimeFormat('en-GB', { weekday: 'long', ...zone }).format(date),
      weekdayShort: new Intl.DateTimeFormat('en-GB', { weekday: 'short', ...zone }).format(date),
      time: new Intl.DateTimeFormat('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true, ...zone }).format(date),
    };
  }

  function formatWhen(iso) {
    const bits = dateBits(iso);
    return `${bits.weekday} ${bits.day} ${bits.month} · ${bits.time}`;
  }

  function seatsLeft(event) {
    if (event?.ticket_types?.length) {
      return event.ticket_types.reduce((sum, type) => sum + Number(type.available || 0), 0);
    }
    return Math.max(0, Number(event?.capacity || 0) - Number(event?.allocated || 0));
  }

  function fromPrice(event) {
    if (event?.from_price != null) return Number(event.from_price);
    const prices = (event?.ticket_types || []).map((type) => Number(type.price));
    return prices.length ? Math.min(...prices) : 0;
  }

  function statusLabel(status, paymentStatus) {
    if (status === 'pending_confirmation') return 'Request received';
    if (status === 'pending_payment' || status === 'pending') return 'Awaiting payment';
    if (status === 'confirmed' && paymentStatus === 'paid') return 'Confirmed';
    if (status === 'expired') return 'Expired';
    if (status === 'declined') return 'Declined';
    if (status === 'cancelled') return 'Cancelled';
    if (status === 'refunded') return 'Refunded';
    return status || 'Booking';
  }

  function statusClass(status) {
    if (status === 'confirmed') return '';
    if (status === 'pending_confirmation' || status === 'pending_payment' || status === 'pending') return 'tastings-ticket-status--pending';
    return 'tastings-ticket-status--expired';
  }

  function randomHex(bytes) {
    const buffer = new Uint8Array(bytes);
    crypto.getRandomValues(buffer);
    return Array.from(buffer, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  function tokenKey(id) {
    return `event-booking:${id}`;
  }

  function saveToken(bookingId, token) {
    if (!bookingId || !token) return;
    localStorage.setItem(tokenKey(bookingId), token);
    const ids = readTokenIds();
    if (!ids.includes(bookingId)) ids.push(bookingId);
    localStorage.setItem(TOKEN_INDEX, JSON.stringify(ids));
  }

  function readToken(bookingId) {
    return localStorage.getItem(tokenKey(bookingId));
  }

  function readTokenIds() {
    try {
      const parsed = JSON.parse(localStorage.getItem(TOKEN_INDEX) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function parseRoute() {
    const hashless = !(location.hash || '').replace(/^#\/?/, '');
    if (routeConfig.pathRouting || hashless) {
      const query = new URLSearchParams(location.search.replace(/^\?/, ''));
      const parts = routeConfig.pathRouting ? location.pathname.replace(/\/+$/, '').split('/').filter(Boolean) : [];
      const screen = query.get('screen') || query.get('view') || '';
      const dateFromQuery = query.get('date') || query.get('event') || '';
      const dateFromPath = parts[0] === 'tastings' && parts[1] && parts[1] !== 'checkout' ? parts[1] : '';
      const date = dateFromQuery || dateFromPath;
      const calendar = screen === 'calendar' || (parts[0] === 'tastings' && parts[1] === 'events');
      const checkout = screen === 'checkout' || parts.includes('checkout');
      if (calendar && !date) {
        return { path: '/events', parts: ['events'], query };
      }
      if (checkout) {
        const key = date || sessionStorage.getItem(CHECKOUT_EVENT_KEY) || '';
        return { path: key ? `/checkout/${key}` : '/checkout', parts: ['checkout', key].filter(Boolean), query };
      }
      if (date) {
        return { path: `/event/${date}`, parts: ['event', date], query };
      }
      return { path: '/', parts: [], query };
    }
    const raw = (location.hash || '').replace(/^#/, '') || '/';
    const [pathPart, queryPart] = raw.split('?');
    const parts = (pathPart || '/').split('/').filter(Boolean);
    return {
      path: parts.length ? `/${parts.join('/')}` : '/',
      parts,
      query: new URLSearchParams(queryPart || location.search.replace(/^\?/, '')),
    };
  }

  function readReturnQuery() {
    const search = new URLSearchParams(location.search);
    return {
      bookingId: search.get('booking_id') || search.get('bookingId') || search.get('booking') || search.get('id'),
      token: search.get('token') || search.get('access_token'),
      payment: search.get('payment'),
      complete:
        search.get('booking_complete') === '1' ||
        search.get('screen') === 'booking-complete' ||
        search.get('view') === 'booking-complete' ||
        /booking-complete/.test(location.pathname),
    };
  }

  async function readJson(response) {
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  function decorateEvent(event) {
    if (!event) return event;
    const showcase = event.showcase_content || {};
    return {
      ...event,
      kicker: event.kicker || showcase.kicker || null,
      tags: event.tags?.length ? event.tags : showcase.tags || [],
      summary: event.summary || event.excerpt || '',
    };
  }

  function flattenPlaced(data) {
    const booking = data?.booking && typeof data.booking === 'object' ? data.booking : data;
    return {
      ...(booking || {}),
      booking_id: booking?.booking_id || booking?.id,
      next: data?.next,
      pay_url: data?.pay_url ?? null,
    };
  }

  function bookingRecord(data) {
    if (!data) return null;
    const booking = data.booking && typeof data.booking === 'object' ? data.booking : data;
    if (!booking || !(booking.id || booking.booking_id)) return null;
    return {
      booking: {
        ...booking,
        id: booking.id || booking.booking_id,
      },
      event: data.event || booking.event || {},
      tickets: data.tickets || booking.tickets || [],
    };
  }

  function placedEnvelope(placed, event, payUrl) {
    const mode = event?.booking_mode || 'request_then_pay';
    let next = 'request_received';
    let pay_url = null;
    if (mode === 'request_then_pay') next = 'request_received';
    else if (placed.status === 'confirmed' || Number(placed.total_amount) === 0) next = 'confirmed';
    else {
      next = 'pay';
      pay_url = payUrl || null;
    }
    return { booking: placed, pay_url, next };
  }

  function createHttpApi(base) {
    const root = String(base || '').replace(/\/$/, '');
    let catalogCache = null;

    async function request(path, { method = 'GET', body } = {}) {
      const headers = { Accept: 'application/json' };
      if (method !== 'GET') headers['Content-Type'] = 'application/json';
      let response;
      try {
        response = await fetch(root + path, {
          method,
          headers,
          body: body == null ? undefined : JSON.stringify(body),
        });
      } catch {
        throw new Error('Could not reach Event Bookings. Check the API base URL, or switch the section to In-theme mock.');
      }
      const data = await readJson(response);
      if (response.status === 404) {
        if (method !== 'GET') throw new Error(data?.error || 'Not found');
        return null;
      }
      if (response.status === 403) {
        throw new Error(data?.error || 'This page origin is not allowed to call Event Bookings. Add it in POS → Settings → Event Bookings → Allowed origins.');
      }
      if (!response.ok) {
        throw new Error(data?.error || data?.message || `Request failed (${response.status})`);
      }
      return data;
    }

    async function catalog() {
      if (!catalogCache) {
        const data = await request('');
        catalogCache = {
          store: data?.store || { found: false },
          events: Array.isArray(data?.events) ? data.events.map(decorateEvent) : [],
        };
      }
      return catalogCache;
    }

    return {
      mode: 'http',
      async resolveStore() {
        return (await catalog()).store;
      },
      async listEvents() {
        return (await catalog()).events;
      },
      async getEvent(eventSlug) {
        const data = await request(`/events/${encodeURIComponent(eventSlug)}`);
        if (!data) return null;
        return decorateEvent(data.event || data);
      },
      async placeBooking(payload) {
        catalogCache = null;
        return request('/bookings', { method: 'POST', body: payload });
      },
      async getBooking(bookingId, token) {
        return bookingRecord(await request(`/bookings/${encodeURIComponent(bookingId)}?token=${encodeURIComponent(token)}`));
      },
      async requestRefund(bookingId, token, note) {
        return request(`/bookings/${encodeURIComponent(bookingId)}/refund`, {
          method: 'POST',
          body: { token, note },
        });
      },
    };
  }

  function createShopifyCatalog(catalogue) {
    const store = { found: true, currency: 'GBP', ...(catalogue?.store || {}) };
    const events = Array.isArray(catalogue?.events) ? catalogue.events.map(decorateEvent) : [];
    const bySlug = new Map();
    for (const event of events) {
      if (event?.slug) bySlug.set(event.slug, event);
      if (event?.date_path) bySlug.set(event.date_path, event);
    }
    return {
      mode: 'shopify',
      async resolveStore() {
        return store;
      },
      async listEvents() {
        return events;
      },
      async getEvent(eventSlug) {
        return bySlug.get(eventSlug) || null;
      },
    };
  }

  function createStorefrontCatalog(config) {
    let pending = null;
    function load() {
      if (!pending) {
        const storefront = window.MilroysTastingsStorefront;
        if (!storefront) return Promise.reject(new Error('The tastings calendar script did not load.'));
        pending = storefront
          .fetchStorefrontCatalogue({
            domain: config.storefrontDomain,
            token: config.storefrontToken,
            collection: config.storefrontCollection,
            apiVersion: config.storefrontApiVersion,
            shopOrigin: String(config.purchaseOrigin || 'https://3greekstreet.com').replace(/\/$/, ''),
          })
          .then(createShopifyCatalog)
          .catch((error) => {
            pending = null;
            throw error;
          });
      }
      return pending;
    }
    return {
      mode: 'shopify',
      async resolveStore() {
        return (await load()).resolveStore();
      },
      async listEvents() {
        return (await load()).listEvents();
      },
      async getEvent(eventSlug) {
        return (await load()).getEvent(eventSlug);
      },
    };
  }

  function createBrowserMock(catalogue, slug) {
    const store = structuredClone(catalogue.store);
    const events = structuredClone(catalogue.events);
    let bookings = [];
    try {
      bookings = JSON.parse(localStorage.getItem(MOCK_BOOKINGS_KEY) || '[]');
      if (!Array.isArray(bookings)) bookings = [];
    } catch {
      bookings = [];
    }

    const save = () => localStorage.setItem(MOCK_BOOKINGS_KEY, JSON.stringify(bookings));

    const now = () => new Date();

    function countsAgainst(booking, when) {
      if (['cancelled', 'declined', 'expired', 'refunded'].includes(booking.status)) return false;
      const pending = ['pending_confirmation', 'pending_payment', 'pending'].includes(booking.status);
      if (pending && booking.hold_expires_at && when > new Date(booking.hold_expires_at)) return false;
      return true;
    }

    function expireHolds() {
      const when = now();
      let changed = false;
      for (const booking of bookings) {
        const pending = ['pending_confirmation', 'pending_payment', 'pending'].includes(booking.status);
        if (pending && booking.hold_expires_at && when > new Date(booking.hold_expires_at)) {
          booking.status = 'expired';
          for (const ticket of booking.tickets || []) {
            if (ticket.status === 'held') ticket.status = 'cancelled';
          }
          changed = true;
        }
      }
      if (changed) save();
    }

    function ticketUsed(typeId) {
      const when = now();
      let used = 0;
      for (const booking of bookings) {
        if (!countsAgainst(booking, when)) continue;
        used += (booking.tickets || []).filter(
          (ticket) => ticket.ticket_type_id === typeId && !['cancelled', 'refunded'].includes(ticket.status),
        ).length;
      }
      return used;
    }

    function eventUsed(eventId) {
      const when = now();
      let used = 0;
      for (const booking of bookings) {
        if (booking.event_id !== eventId || !countsAgainst(booking, when)) continue;
        used += (booking.tickets || []).filter((ticket) => !['cancelled', 'refunded'].includes(ticket.status)).length;
      }
      return used;
    }

    function onSale(event) {
      const when = now();
      if (event.status !== 'published' || event.visibility === 'private') return false;
      if (event.booking_cutoff_at && when > new Date(event.booking_cutoff_at)) return false;
      if (event.sales_start_at && when < new Date(event.sales_start_at)) return false;
      if (event.sales_end_at && when > new Date(event.sales_end_at)) return false;
      return true;
    }

    function publicEvent(event) {
      return {
        ...event,
        allocated: eventUsed(event.id),
        ticket_types: (event.ticket_types || []).map((type) => {
          const quantity = Number(type.quantity ?? type.capacity ?? 0);
          const available = Math.max(0, quantity - ticketUsed(type.id));
          return { ...type, available, sold_out: available <= 0 };
        }),
      };
    }

    function toList(event) {
      const showcase = event.showcase_content || {};
      const prices = (event.ticket_types || []).map((type) => Number(type.price));
      return {
        id: event.id,
        slug: event.slug,
        title: event.title,
        name: event.name || event.title,
        summary: event.summary,
        image_url: event.image_url,
        venue_name: event.venue_name,
        starts_at: event.starts_at,
        ends_at: event.ends_at,
        visibility: event.visibility,
        booking_mode: event.booking_mode,
        capacity: event.capacity,
        allocated: eventUsed(event.id),
        kicker: showcase.kicker || null,
        tags: showcase.tags || [],
        theme: showcase.theme || 'navy',
        from_price: prices.length ? Math.min(...prices) : 0,
      };
    }

    function bookingPayload(booking) {
      const event = events.find((row) => row.id === booking.event_id);
      return {
        booking: {
          id: booking.id,
          booking_number: booking.booking_number,
          status: booking.status,
          payment_status: booking.payment_status,
          total_amount: booking.total_amount,
          tax_amount: booking.tax_amount,
          currency: booking.currency,
          hold_expires_at: booking.hold_expires_at,
          customer_name: booking.customer_name,
          customer_email: booking.customer_email,
          customer_phone: booking.customer_phone,
          products: booking.products,
          refund_request: booking.refund_request,
        },
        event: event
          ? {
              id: event.id,
              slug: event.slug,
              name: event.name || event.title,
              starts_at: event.starts_at,
              ends_at: event.ends_at,
              venue_name: event.venue_name,
              venue_address: event.venue_address,
              image_url: event.image_url,
              booking_mode: event.booking_mode,
              settings: event.settings,
            }
          : null,
        tickets: (booking.tickets || []).map((ticket) => ({
          id: ticket.id,
          ticket_code: ticket.ticket_code,
          status: ticket.status,
          guest_name: ticket.guest_name,
        })),
      };
    }

    return {
      mode: 'mock',
      async resolveStore() {
        expireHolds();
        if (slug !== store.slug) return { found: false, slug };
        return structuredClone(store);
      },
      async listEvents() {
        expireHolds();
        if (slug !== store.slug) return [];
        return events.filter((event) => event.visibility === 'public' && onSale(event)).map(toList);
      },
      async getEvent(eventSlug) {
        expireHolds();
        const event = events.find((row) => row.slug === eventSlug);
        if (!event || !['public', 'unlisted'].includes(event.visibility) || event.status !== 'published') return null;
        return publicEvent(event);
      },
      async placeBooking(request) {
        expireHolds();
        const customerName = String(request.customer_name || '').trim();
        const customerEmail = String(request.customer_email || '').trim().toLowerCase();
        const ticketLines = request.tickets || [];
        if (!customerName || !customerEmail || !ticketLines.length) throw new Error('invalid booking request');
        const event = events.find((row) => row.id === request.event_id || row.slug === request.event_slug);
        if (!event) throw new Error('event not found');
        if (!onSale(event) || event.visibility === 'private') throw new Error('event is not on sale');
        const settings = { ...store.settings, ...(event.settings || {}) };
        if (settings.require_phone && !String(request.customer_phone || '').trim()) throw new Error('phone required');

        const expanded = [];
        const seen = new Set();
        for (const line of ticketLines) {
          if (!line.ticket_type_id || seen.has(line.ticket_type_id)) throw new Error('invalid ticket selection');
          seen.add(line.ticket_type_id);
          const type = (event.ticket_types || []).find((row) => row.id === line.ticket_type_id);
          const quantity = Number(line.quantity || 0);
          if (!type || quantity < Number(type.min_per_booking || 1) || quantity > Number(type.max_per_booking || 99)) {
            throw new Error('invalid ticket selection');
          }
          const remaining = Number(type.quantity ?? type.capacity ?? 0) - ticketUsed(type.id);
          if (quantity > remaining) throw new Error('not enough ticket capacity');
          for (let i = 0; i < quantity; i += 1) expanded.push(type);
        }
        if (eventUsed(event.id) + expanded.length > Number(event.capacity || 0)) throw new Error('not enough event capacity');

        const guests = request.guests || [];
        if (settings.require_named_tickets && (guests.length !== expanded.length || guests.some((guest) => !String(guest.name || '').trim()))) {
          throw new Error('guest names required');
        }

        const productLines = [];
        for (const line of request.products || []) {
          const product = (event.products || []).find((row) => row.id === line.product_id);
          const quantity = Number(line.quantity || 0);
          if (!product || product.role !== 'upsell' || quantity < 1 || quantity > Number(product.max_per_booking || 1)) {
            throw new Error('invalid product selection');
          }
          productLines.push({ ...product, quantity });
        }

        const total = expanded.reduce((sum, type) => sum + Number(type.price || 0), 0)
          + productLines.reduce((sum, row) => sum + Number(row.price || 0) * row.quantity, 0);
        const totalAmount = Math.round(total * 100) / 100;
        const mode = event.booking_mode || 'request_then_pay';
        const freeInstant = mode === 'instant_purchase' && totalAmount === 0;
        const status = mode === 'request_then_pay' ? 'pending_confirmation' : freeInstant ? 'confirmed' : 'pending_payment';
        const id = crypto.randomUUID();
        const accessToken = randomHex(32);
        const hold = new Date(now().getTime() + Number(store.settings.default_hold_minutes || 15) * 60000);
        const tickets = expanded.map((type, index) => ({
          id: crypto.randomUUID(),
          ticket_type_id: type.id,
          ticket_code: randomHex(4).toUpperCase(),
          guest_name: String(guests[index]?.name || '').trim() || (index === 0 ? customerName : null),
          status: freeInstant ? 'active' : 'held',
        }));
        const booking = {
          id,
          booking_id: id,
          event_id: event.id,
          customer_name: customerName,
          customer_email: customerEmail,
          customer_phone: String(request.customer_phone || '').trim() || null,
          booking_number: randomHex(5).toUpperCase(),
          status,
          payment_status: freeInstant ? 'paid' : 'unpaid',
          total_amount: totalAmount,
          tax_amount: Math.round((totalAmount / 6) * 100) / 100,
          currency: store.currency || 'GBP',
          hold_expires_at: freeInstant ? null : hold.toISOString(),
          access_token: accessToken,
          return_url: String(request.return_url || '').trim() || null,
          email_notification_id: crypto.randomUUID(),
          products: productLines.map((row) => ({
            product_id: row.id,
            name: row.name,
            quantity: row.quantity,
            price: row.price,
          })),
          tickets,
          refund_request: null,
        };
        bookings.push(booking);
        save();
        const placed = {
          booking_id: id,
          id,
          booking_number: booking.booking_number,
          status: booking.status,
          payment_status: booking.payment_status,
          total_amount: booking.total_amount,
          tax_amount: booking.tax_amount,
          currency: booking.currency,
          hold_expires_at: booking.hold_expires_at,
          access_token: accessToken,
          email_notification_id: booking.email_notification_id,
          tickets: tickets.map((ticket) => ({
            id: ticket.id,
            ticket_code: ticket.ticket_code,
            guest_name: ticket.guest_name,
            status: ticket.status,
          })),
        };
        return placedEnvelope(placed, event, `#/mock-pay/${id}?token=${encodeURIComponent(accessToken)}`);
      },
      async getBooking(bookingId, token) {
        expireHolds();
        const booking = bookings.find((row) => row.access_token === token);
        if (!booking || (bookingId && booking.id !== bookingId)) return null;
        return bookingPayload(booking);
      },
      async lookup(bookingNumber, email) {
        expireHolds();
        const booking = bookings.find(
          (row) =>
            String(row.booking_number).toUpperCase() === String(bookingNumber || '').toUpperCase() &&
            row.customer_email === String(email || '').trim().toLowerCase(),
        );
        return booking ? { ...bookingPayload(booking), access_token: booking.access_token } : null;
      },
      async startPayment({ bookingId, accessToken, successUrl, cancelUrl }) {
        const booking = bookings.find((row) => row.id === bookingId && row.access_token === accessToken);
        if (!booking) throw new Error('booking not found');
        const payable =
          ['pending', 'pending_payment'].includes(booking.status) ||
          (booking.status === 'confirmed' && booking.payment_status !== 'paid');
        if (!payable || Number(booking.total_amount) <= 0) throw new Error('booking is not payable');
        const payUrl = new URL(window.location.href);
        payUrl.hash = `#/mock-pay/${bookingId}?token=${encodeURIComponent(accessToken)}&success=${encodeURIComponent(successUrl)}&cancel=${encodeURIComponent(cancelUrl)}`;
        return { payment_url: payUrl.toString(), session_id: `mock_${bookingId.slice(0, 8)}`, provider: 'mock' };
      },
      async activatePayment(bookingId, token) {
        const booking = bookings.find((row) => row.id === bookingId && row.access_token === token);
        if (!booking) throw new Error('booking not found');
        booking.status = 'confirmed';
        booking.payment_status = 'paid';
        booking.hold_expires_at = null;
        for (const ticket of booking.tickets || []) {
          if (ticket.status === 'held') ticket.status = 'active';
        }
        save();
        return bookingPayload(booking);
      },
      async requestRefund(bookingId, token, note) {
        const payload = await this.getBooking(bookingId, token);
        if (!payload) throw new Error('booking not found');
        const booking = bookings.find((row) => row.id === payload.booking.id);
        const event = events.find((row) => row.id === booking.event_id);
        const settings = { ...store.settings, ...(event?.settings || {}) };
        if (!settings.allow_refunds) throw new Error('refunds are not allowed');
        if (booking.status !== 'confirmed' || booking.payment_status !== 'paid') throw new Error('booking is not refundable');
        const cutoff = new Date(new Date(event.starts_at).getTime() - Number(settings.refund_cutoff_hours || 24) * 3600000);
        if (now() > cutoff) throw new Error('refund cutoff has passed');
        booking.refund_request = { status: 'pending', note: String(note || ''), requested_at: now().toISOString() };
        save();
        return { requested: true, booking_id: booking.id };
      },
      async sendEmail() {},
    };
  }

  let embedPageUrl = '';

  function withPreview(href, origin) {
    const preview =
      new URLSearchParams(location.search).get('preview_theme_id') ||
      String(routeConfig.purchaseThemeId || '').trim();
    if (!preview) return href;
    try {
      const parsed = new URL(href, origin || location.origin);
      parsed.searchParams.set('preview_theme_id', preview);
      if (parsed.origin === location.origin) return `${parsed.pathname}${parsed.search}${parsed.hash}`;
      return parsed.toString();
    } catch {
      return href;
    }
  }

  function go(path) {
    const next = path.startsWith('#') ? path : `#${path}`;
    if (embedPageUrl) {
      const target = new URL(embedPageUrl, window.location.origin);
      const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (preview) target.searchParams.set('preview_theme_id', preview);
      window.location.assign(`${target.pathname}${target.search}${next}`);
      return;
    }
    if (routeConfig.pathRouting) {
      const parts = String(path || '').replace(/^#/, '').split('/').filter(Boolean);
      const url = new URL('/pages/tastings', location.origin);
      const preview = new URLSearchParams(location.search).get('preview_theme_id');
      if (!parts.length) {
        window.location.assign(`${url.pathname}${preview ? `?preview_theme_id=${encodeURIComponent(preview)}` : ''}`);
        return;
      }
      if (parts[0] === 'checkout') {
        url.searchParams.set('view', 'checkout');
        if (parts[1]) {
          sessionStorage.setItem(CHECKOUT_EVENT_KEY, parts[1]);
          url.searchParams.set('date', parts[1]);
        }
      } else if (parts[0] === 'events') {
        url.searchParams.set('view', 'calendar');
      } else if (parts[0] === 'event' && parts[1]) {
        url.searchParams.set('view', 'event');
        url.searchParams.set('date', parts[1]);
      } else {
        window.location.assign(`${url.pathname}${preview ? `?preview_theme_id=${encodeURIComponent(preview)}` : ''}`);
        return;
      }
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const nextUrl = `${url.pathname}${url.search}`;
      if (`${location.pathname}${location.search}` === nextUrl) {
        window.dispatchEvent(new Event('tastings:route'));
        return;
      }
      window.location.assign(nextUrl);
      return;
    }
    if (`#${location.hash.replace(/^#/, '')}` === next || location.hash === next) {
      window.dispatchEvent(new HashChangeEvent('hashchange'));
      return;
    }
    location.hash = next.replace(/^#/, '#');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function goToPayment(url) {
    try {
      const parsed = new URL(url, window.location.href);
      if (parsed.origin === window.location.origin && parsed.hash) {
        location.hash = parsed.hash;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    } catch {
      /* fall through */
    }
    (window.top || window).location.assign(url);
  }

  function availabilityCopy(event) {
    if (isNotice(event)) {
      return {
        label: event.notice_label || 'On the calendar',
        remaining: event.notice_detail || '',
        sold: false,
        notice: true,
      };
    }
    const left = seatsLeft(event);
    if (left <= 0) return { label: 'Sold out', remaining: 'This tasting is fully booked', sold: true };
    if (event?.seats_known === false) return { label: 'Tickets available', remaining: 'Seats available', sold: false };
    return { label: 'Tickets available', remaining: `${left} seat${left === 1 ? '' : 's'} remaining`, sold: false };
  }

  window.MilroysTastings = {
    async init(config) {
      routeConfig = config || {};
      const root = config.root;
      if (!root) return;
      const copy = config.copy || {};
      const slug = config.storeSlug || 'milroys-of-soho';
      const hostedBase = String(config.hostedBase || `https://events.order2go.co.uk/${slug}`).replace(/\/$/, '');
      function isStockCover(url) {
        return /images\.unsplash\.com\/photo-1569529465841/.test(String(url || ''));
      }

      function coverUrl(event) {
        const own = event && event.image_url;
        if (own && !isStockCover(own)) return own;
        const configured = config.coverUrl || '';
        if (configured && !isStockCover(configured)) return configured;
        return config.roomCoverUrl || config.calendarHeroUrl || '';
      }

      function coverImg(event, alt = '', eager = false) {
        return `<img src="${escapeHtml(coverUrl(event))}" alt="${escapeHtml(alt)}" width="1600" height="1000" ${eager ? 'loading="eager"' : 'loading="lazy"'} decoding="async">`;
      }

      function coverMedia(event, extraClass = '', alt = '', eager = false) {
        const classes = ['tastings-cover', extraClass].filter(Boolean).join(' ');
        return `<div class="${classes}">${coverImg(event, alt, eager)}</div>`;
      }

      let api;
      if (config.mode === 'mock') {
        const catalogue = await fetch(config.mockDataUrl).then((response) => response.json());
        api = createBrowserMock(catalogue, catalogue.store?.slug || 'milroys');
      } else if (config.mode === 'shopify') {
        const catalogueUrl = config.shopifyCatalogUrl || config.mockDataUrl;
        const catalogue = await fetch(catalogueUrl).then((response) => {
          if (!response.ok) throw new Error('Could not load the 3 Greek Street tasting calendar.');
          return response.json();
        });
        api = createShopifyCatalog(catalogue);
      } else if (config.mode === 'storefront') {
        api = createStorefrontCatalog(config);
      } else {
        api = createHttpApi(config.apiBase);
      }
      const shopify = config.mode === 'shopify' || config.mode === 'storefront';
      const tastingsHome = config.tastingsPath || '/pages/tastings';
      const purchaseOrigin = String(config.purchaseOrigin || 'https://3greekstreet.com').replace(/\/$/, '');
      const accountUrl = config.accountUrl || `${purchaseOrigin}/account`;

      function tastingPath(event) {
        return event?.date_path || event?.slug || '';
      }

      function purchaseHref(event, view = 'event') {
        const own = view === 'checkout' ? event?.checkout_url : event?.event_url;
        if (own) return withPreview(own, purchaseOrigin);
        const path = `/pages/tastings?view=${view}&date=${encodeURIComponent(tastingPath(event))}`;
        return withPreview(`${purchaseOrigin}${path}`, purchaseOrigin);
      }

      function shopifyEventHref(event) {
        if (!event) return withPreview(`${purchaseOrigin}/pages/tastings`, purchaseOrigin);
        if (!config.pathRouting) return purchaseHref(event);
        return withPreview(`/pages/tastings?view=event&date=${encodeURIComponent(tastingPath(event))}`, location.origin);
      }

      function shopifyBookHref(event) {
        if (!event) return shopifyEventHref(event);
        const key = tastingPath(event);
        if (config.pathRouting) {
          sessionStorage.setItem(CHECKOUT_EVENT_KEY, key);
          return withPreview(`/pages/tastings?view=checkout&date=${encodeURIComponent(key)}`, location.origin);
        }
        return purchaseHref(event, 'checkout');
      }

      function shopifyBookLink(event, label, className) {
        const href = shopifyBookHref(event);
        if (!href) return '';
        const local = config.pathRouting ? '' : ' target="_top" rel="noopener"';
        return `<a class="${className || 'tastings-btn tastings-btn--gold'}" href="${escapeHtml(href)}"${local}>${escapeHtml(label)}</a>`;
      }

      function accountLink(label, className) {
        return `<a class="${className || 'tastings-btn'}" href="${escapeHtml(accountUrl)}" target="_top" rel="noopener">${escapeHtml(label)}</a>`;
      }

      const state = {
        store: null,
        events: [],
        details: new Map(),
        error: '',
        checkout: null,
        placing: false,
        filter: 'All tastings',
        month: '',
        sort: 'soonest',
        menu: '',
        modal: null,
      };

      function buildReturnUrl() {
        const custom = String(config.returnUrl || '').trim();
        const shop = String(config.shopUrl || window.location.origin).replace(/\/$/, '');
        const url = custom
          ? new URL(custom, `${shop}/`)
          : new URL(`${shop}/pages/tastings`);
        const current = new URLSearchParams(window.location.search);
        if (!custom) url.searchParams.set('view', 'booking-complete');
        const preview = current.get('preview_theme_id') || config.themeId;
        if (preview && !url.searchParams.has('preview_theme_id')) {
          url.searchParams.set('preview_theme_id', String(preview));
        }
        return url.toString();
      }

      function payHref(bookingId) {
        const token = readToken(bookingId);
        if (!token) return '';
        if (config.mode === 'mock') {
          return `#/mock-pay/${encodeURIComponent(bookingId)}?token=${encodeURIComponent(token)}`;
        }
        if (config.mode === 'mock_server') {
          const origin = new URL(config.apiBase, window.location.href).origin;
          return `${origin}/mock-checkout?booking_id=${encodeURIComponent(bookingId)}&token=${encodeURIComponent(token)}`;
        }
        return `${hostedBase}/pay/${encodeURIComponent(bookingId)}?token=${encodeURIComponent(token)}`;
      }

      function payLink(bookingId, label) {
        const href = payHref(bookingId);
        if (!href) return '';
        const top = href.startsWith('http') ? ' target="_top" rel="noopener"' : '';
        return `<a class="tastings-btn tastings-btn--gold" href="${escapeHtml(href)}"${top}>${escapeHtml(label)}</a>`;
      }

      function payUrls(bookingId, token) {
        const encoded = encodeURIComponent(token);
        if (hostedBase && api.mode === 'http') {
          return {
            success: `${hostedBase}/pay/${bookingId}?token=${encoded}`,
            cancel: `${hostedBase}/pay/${bookingId}?token=${encoded}`,
          };
        }
        const url = new URL(window.location.origin + (config.pagePath || window.location.pathname));
        url.search = window.location.search;
        return {
          success: `${url.toString()}#/pay/${bookingId}?token=${encoded}&payment=success`,
          cancel: `${url.toString()}#/pay/${bookingId}?token=${encoded}&payment=cancelled`,
        };
      }

      async function loadCatalog() {
        state.store = await api.resolveStore();
        if (!state.store?.found) {
          state.events = [];
          return;
        }
        state.events = (await api.listEvents()).map(decorateEvent);
        for (const event of state.events) {
          if (event?.slug) state.details.set(event.slug, event);
          if (event?.date_path) state.details.set(event.date_path, event);
        }
      }

      async function eventBySlug(eventSlug) {
        if (state.details.has(eventSlug)) return decorateEvent(state.details.get(eventSlug));
        const detail = decorateEvent(await api.getEvent(eventSlug));
        if (detail) state.details.set(eventSlug, detail);
        return detail;
      }

      function mergedEvent(listEvent) {
        return { ...(listEvent || {}), ...(state.details.get(listEvent?.slug) || {}) };
      }

      function currentEvents() {
        return state.events.map(mergedEvent).filter((event) => isCurrentEvent(event));
      }

      function tags() {
        const values = new Set();
        currentEvents().forEach((event) => (event.tags || []).forEach((tag) => values.add(tag)));
        return ['All tastings', ...values];
      }

      function filteredEvents() {
        let list = currentEvents();
        if (state.filter !== 'All tastings') {
          list = list.filter((event) => (event.tags || []).includes(state.filter));
        }
        if (state.month) {
          list = list.filter((event) => monthKey(event.starts_at) === state.month);
        }
        if (state.sort === 'latest') list = [...list].sort((a, b) => new Date(b.starts_at) - new Date(a.starts_at));
        else if (state.sort === 'price') list = [...list].sort((a, b) => fromPrice(a) - fromPrice(b));
        else if (state.sort === 'availability') list = [...list].sort((a, b) => seatsLeft(b) - seatsLeft(a));
        else list = [...list].sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
        return list;
      }

      function card(event) {
        const bits = dateBits(event.starts_at);
        const avail = availabilityCopy(event);
        const href = shopify ? shopifyEventHref(event) : '';
        const open = shopify
          ? `<a class="tastings-event-card" href="${escapeHtml(href)}">`
          : `<button class="tastings-event-card" type="button" data-go="/event/${escapeHtml(event.slug)}">`;
        const close = shopify ? '</a>' : '</button>';
        return `
          ${open}
            <div class="tastings-event-image">
              ${coverMedia(event, '', event.title)}
              <div class="tastings-date-chip"><b>${escapeHtml(bits.day)}</b><span>${escapeHtml(bits.monthShort)}</span></div>
              <div class="tastings-event-series">${escapeHtml(event.kicker || 'Guided tasting')}</div>
            </div>
            <div class="tastings-event-card-content">
              <h3>${escapeHtml(event.title)}</h3>
              <p>${escapeHtml(event.summary || '')}</p>
              <div class="tastings-card-bottom">
                <span class="tastings-card-price">${escapeHtml(avail.notice ? avail.label : money(fromPrice(event), state.store?.currency))}</span>
                <span class="tastings-status">${escapeHtml(avail.notice ? avail.remaining : avail.sold ? 'Sold out' : avail.remaining)}</span>
              </div>
            </div>
          ${close}`;
      }

      function landingView() {
        const upcoming = spotlightEvents();
        const featured = upcoming[0];
        const rest = upcoming.slice(1, 4);
        const fullCalendar = !!routeConfig.pathRouting;
        const heroTarget = featured ? `/event/${featured.date_path || featured.slug}` : '/events';
        return `
          <div class="tastings-page">
            <div class="tastings-hero">
              <div class="tastings-container tastings-hero-grid">
                <div>
                  <p class="tastings-eyebrow">${escapeHtml(copy.eyebrow)}</p>
                  <h1>${escapeHtml(copy.heading)}</h1>
                  <p class="tastings-hero-copy">${escapeHtml(copy.lede)}</p>
                  <div class="tastings-btn-row">
                    ${shopify && featured
                    ? `<a class="tastings-btn tastings-btn--primary" href="${escapeHtml(shopifyEventHref(featured))}">${escapeHtml(copy.primaryCta)} →</a>`
                    : `<button class="tastings-btn tastings-btn--primary" type="button" data-go="${escapeHtml(heroTarget)}">${escapeHtml(copy.primaryCta)} →</button>`}
                    ${shopify ? accountLink(copy.secondaryCta || 'Your tickets') : `<button class="tastings-btn" type="button" data-go="/bookings">${escapeHtml(copy.secondaryCta)}</button>`}
                  </div>
                </div>
                <div class="tastings-hero-art" aria-hidden="true">
                  <div class="tastings-art-stamp">Whisky · Stories · Soho</div>
                  <div class="tastings-bottle"><div class="tastings-bottle-label">MILROY'S<br>TASTING ROOM</div></div>
                  <div class="tastings-pour-count"><strong>6</strong>drams, one table</div>
                </div>
              </div>
            </div>
            ${featured ? featuredBlock(featured) : ''}
            ${fullCalendar ? `
            <div class="tastings-section" id="tastings-calendar">
              <div class="tastings-container">
                <div class="tastings-section-head">
                  <div>
                    <p class="tastings-eyebrow">The calendar</p>
                    <h2>${escapeHtml(copy.upcomingHeading || 'Future tastings')}</h2>
                    <p class="tastings-section-sub">${escapeHtml(copy.upcomingSub || 'Every tasting currently on sale.')}</p>
                  </div>
                </div>
                ${calendarListHtml()}
              </div>
            </div>` : `
            <div class="tastings-section">
              <div class="tastings-container">
                <div class="tastings-section-head">
                  <div>
                    <p class="tastings-eyebrow">Coming soon</p>
                    <h2>${escapeHtml(copy.upcomingHeading)}</h2>
                    <p class="tastings-section-sub">${escapeHtml(copy.upcomingSub)}</p>
                  </div>
                  <button class="tastings-text-link" type="button" data-go="/events">See the full calendar</button>
                </div>
                <div class="tastings-event-grid">${rest.map(card).join('') || '<p>No further tastings are on sale yet.</p>'}</div>
              </div>
            </div>`}
            <div class="tastings-section tastings-section--white">
              <div class="tastings-container">
                <div class="tastings-manage-band">
                  <div>
                    <p class="tastings-eyebrow">Already booked?</p>
                    <h2>${escapeHtml(copy.manageHeading)}</h2>
                    <p>${escapeHtml(copy.manageCopy)}</p>
                  </div>
                  ${shopify ? accountLink('Your tickets & bookings', 'tastings-btn tastings-btn--primary') : '<button class="tastings-btn tastings-btn--primary" type="button" data-go="/bookings">Manage my tickets &amp; bookings</button>'}
                </div>
              </div>
            </div>
          </div>`;
      }

      function featuredBlock(event) {
        const bits = dateBits(event.starts_at);
        const avail = availabilityCopy(event);
        const showcase = event.showcase_content || {};
        return `
          <div class="tastings-section tastings-section--white">
            <div class="tastings-container">
              <div class="tastings-section-head">
                <div>
                  <p class="tastings-eyebrow">Next up</p>
                  <h2>${escapeHtml(copy.nextHeading)}</h2>
                  <p class="tastings-section-sub">${escapeHtml(copy.nextSub)}</p>
                </div>
                ${config.layout === 'next' || routeConfig.pathRouting
                  ? `<a class="tastings-text-link" href="${escapeHtml(withPreview(`${tastingsHome}?view=calendar`, location.origin))}">View all future events</a>`
                  : '<button class="tastings-text-link" type="button" data-go="/events">View all future events</button>'}
              </div>
              <a class="tastings-next-card" href="${escapeHtml(shopify ? shopifyEventHref(event) : `#/event/${event.date_path || event.slug}`)}">
                <div class="tastings-next-media">
                  ${coverMedia(event, '', event.title)}
                  <div class="tastings-date-chip"><b>${escapeHtml(bits.day)}</b><span>${escapeHtml(bits.monthShort)}</span></div>
                </div>
                <div class="tastings-event-body">
                  <div class="tastings-event-kicker">${escapeHtml(event.kicker || showcase.kicker || 'Guided tasting')}</div>
                  <h3>${escapeHtml(event.title)}</h3>
                  <p>${escapeHtml(event.summary || event.description || '')}</p>
                  <div class="tastings-meta">
                    <span>${clockIcon} ${escapeHtml(bits.weekday)} · ${escapeHtml(bits.time)}</span>
                    <span>${pinIcon} ${escapeHtml(event.venue_address || event.venue_name || '3 Greek Street, Soho')}</span>
                  </div>
                </div>
                <div class="tastings-event-cta">
                  <div>
                    <div class="tastings-price">${avail.notice ? escapeHtml(avail.label) : `${escapeHtml(money(fromPrice(event), state.store?.currency))} <small>per person</small>`}</div>
                    <div class="tastings-availability${avail.sold ? ' tastings-availability--sold' : ''}">${escapeHtml(avail.label)}</div>
                    <div class="tastings-remaining">${escapeHtml(avail.remaining)}</div>
                  </div>
                  <span class="tastings-btn tastings-btn--primary">View tasting</span>
                </div>
              </a>
            </div>
          </div>`;
      }

      function wideEvent(event) {
        const bits = dateBits(event.starts_at);
        const avail = availabilityCopy(event);
        const open = shopify
          ? `<a class="tastings-wide-event" href="${escapeHtml(shopifyEventHref(event))}">`
          : `<button class="tastings-wide-event" type="button" data-go="/event/${escapeHtml(event.date_path || event.slug)}">`;
        const close = shopify ? '</a>' : '</button>';
        return `
          ${open}
            <div class="tastings-wide-media">
              ${coverMedia(event, '', event.title)}
              <div class="tastings-date-chip"><b>${escapeHtml(bits.day)}</b><span>${escapeHtml(bits.monthShort)}</span></div>
            </div>
            <div class="tastings-wide-body">
              <div class="tastings-event-kicker">${escapeHtml(event.kicker || 'Guided tasting')} · ${escapeHtml(bits.weekdayShort)} ${escapeHtml(event.all_day ? 'All day' : bits.time)}</div>
              <h3>${escapeHtml(event.title)}</h3>
              <p>${escapeHtml(event.summary || '')}</p>
              <div class="tastings-wide-footer">
                <span class="tastings-status">${escapeHtml(avail.notice ? avail.remaining : avail.sold ? 'Sold out' : avail.remaining)}</span>
                <span class="tastings-card-price">${escapeHtml(avail.notice ? avail.label : money(fromPrice(event), state.store?.currency))}</span>
              </div>
            </div>
          ${close}`;
      }

      function filterOption(kind, value, label, active, count) {
        return `<button class="tastings-filter-option${active ? ' is-active' : ''}" type="button" data-pick="${escapeHtml(kind)}" data-value="${escapeHtml(value)}"><span>${escapeHtml(label)}</span>${count == null ? '' : `<span class="tastings-filter-count">${count}</span>`}</button>`;
      }

      function calendarListHtml() {
        const list = filteredEvents();
        const sorts = [
          ['soonest', 'Soonest'],
          ['latest', 'Latest'],
          ['price', 'Price: low to high'],
          ['availability', 'Most seats left'],
        ];
        const sortLabel = sorts.find((row) => row[0] === state.sort)?.[1] || 'Soonest';
        const monthCounts = new Map();
        const tagCounts = new Map();
        const listed = currentEvents();
        for (const event of listed) {
          const key = monthKey(event.starts_at);
          if (key) monthCounts.set(key, (monthCounts.get(key) || 0) + 1);
          for (const tag of event.tags || []) tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
        }
        const months = [...monthCounts.keys()].sort();
        const categoryLabel = state.filter === 'All tastings' ? 'All' : state.filter;
        const monthButton = state.month ? monthLabel(state.month, 'short') : 'Any month';
        const filtering = state.filter !== 'All tastings' || state.month || state.sort !== 'soonest';
        const panel = (name, options) => state.menu === name
          ? `<div class="tastings-filter-panel" role="listbox">${options}</div>`
          : '';
        return `
          <div class="tastings-filters">
            <div class="tastings-filter-item${state.menu === 'tag' ? ' is-open' : ''}">
              <button class="tastings-filter${state.filter !== 'All tastings' ? ' is-set' : ''}${state.menu === 'tag' ? ' is-open' : ''}" type="button" data-menu="tag" aria-expanded="${state.menu === 'tag' ? 'true' : 'false'}">
                <span class="tastings-filter__kicker">Category</span>
                <span class="tastings-filter__value"><strong>${escapeHtml(categoryLabel)}</strong></span>
              </button>
              ${panel('tag', [
                filterOption('tag', 'All tastings', 'All tastings', state.filter === 'All tastings', listed.length),
                ...tags().filter((tag) => tag !== 'All tastings').map((tag) => filterOption('tag', tag, tag, state.filter === tag, tagCounts.get(tag) || 0)),
              ].join(''))}
            </div>
            <div class="tastings-filter-item${state.menu === 'month' ? ' is-open' : ''}">
              <button class="tastings-filter${state.month ? ' is-set' : ''}${state.menu === 'month' ? ' is-open' : ''}" type="button" data-menu="month" aria-expanded="${state.menu === 'month' ? 'true' : 'false'}">
                <span class="tastings-filter__kicker">Month</span>
                <span class="tastings-filter__value"><strong>${escapeHtml(monthButton)}</strong></span>
              </button>
              ${panel('month', [
                filterOption('month', '', 'Any month', !state.month, listed.length),
                ...months.map((key) => filterOption('month', key, monthLabel(key), state.month === key, monthCounts.get(key) || 0)),
              ].join(''))}
            </div>
            <div class="tastings-filter-item${state.menu === 'sort' ? ' is-open' : ''}">
              <button class="tastings-filter${state.sort !== 'soonest' ? ' is-set' : ''}${state.menu === 'sort' ? ' is-open' : ''}" type="button" data-menu="sort" aria-expanded="${state.menu === 'sort' ? 'true' : 'false'}">
                <span class="tastings-filter__kicker">Sort</span>
                <span class="tastings-filter__value"><strong>${escapeHtml(sortLabel)}</strong></span>
              </button>
              ${panel('sort', sorts.map(([value, label]) => filterOption('sort', value, label, state.sort === value)).join(''))}
            </div>
          </div>
          <div class="tastings-filter-meta">
            <span>${list.length} ${list.length === 1 ? 'event' : 'events'}</span>
            ${filtering ? '<button class="tastings-filter-clear" type="button" data-clear-filters>Clear</button>' : ''}
          </div>
          <div class="tastings-list-grid">
            ${list.map(wideEvent).join('') || '<p class="tastings-filter-empty">Nothing on the calendar matches that.</p>'}
          </div>`;
      }

      function eventsView() {
        const image = config.calendarHeroUrl || config.coverUrl || '';
        const home = withPreview(tastingsHome, location.origin);
        return `
          <div class="tastings-page">
            <section class="tastings-mini-hero"${image ? ` style="background-image:url('${escapeHtml(image)}')"` : ''}>
              <div class="tastings-mini-hero__inner tastings-container">
                <a class="tastings-mini-hero__back" href="${escapeHtml(home)}">Tastings &amp; experiences</a>
                <p class="tastings-eyebrow">What's on</p>
                <h1>Upcoming tastings</h1>
                <p>Distillery-hosted evenings, barrel-top pours, and ticketed events in the tasting room.</p>
              </div>
            </section>
            <div class="tastings-container tastings-calendar-wrap">
              ${calendarListHtml()}
            </div>
          </div>`;
      }

      function descriptionBlocks(text) {
        return String(text || '')
          .split(/\n+/)
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => `<p>${escapeHtml(line)}</p>`)
          .join('');
      }

      function eventView(event) {
        if (!event) {
          return `<div class="tastings-empty tastings-container"><h2>Tasting not found</h2><p>This event is not on sale, or the link is private.</p><button class="tastings-btn tastings-btn--primary" type="button" data-go="/events">View all tastings</button></div>`;
        }
        const bits = dateBits(event.starts_at);
        const showcase = event.showcase_content || {};
        const avail = availabilityCopy(event);
        const tagsHtml = (showcase.tags || event.tags || []).map((tag) => `<span class="tastings-tag tastings-tag--light">${escapeHtml(tag)}</span>`).join('');
        const extras = [
          showcase.duration ? `<span class="tastings-tag tastings-tag--light">${escapeHtml(showcase.duration)}</span>` : '',
          event.venue_name ? `<span class="tastings-tag tastings-tag--light">${escapeHtml(event.venue_name)}</span>` : '',
        ].join('');
        const tastingList = (showcase.tasting_list || [])
          .map((row) => `<div class="tastings-row"><div class="tastings-num">${escapeHtml(row.num)}</div><div><h4>${escapeHtml(row.title)}</h4><p>${escapeHtml(row.text)}</p></div></div>`)
          .join('');
        const timeline = (showcase.timeline || [])
          .map((row) => `<div class="tastings-timeline-item"><time>${escapeHtml(row.time)}</time><div><h4>${escapeHtml(row.title)}</h4><p>${escapeHtml(row.text)}</p></div></div>`)
          .join('');
        const good = (showcase.good_to_know || [])
          .map((row) => `<div class="tastings-good"><h4>${escapeHtml(row.title)}</h4><p>${escapeHtml(row.text)}</p></div>`)
          .join('');
        const included = (event.products || []).filter((product) => product.role === 'included');
        const bookAction = avail.sold || avail.notice
          ? ''
          : shopify
            ? shopifyBookLink(event, 'Book tickets')
            : `<button class="tastings-btn tastings-btn--gold" type="button" data-go="/checkout/${escapeHtml(event.slug)}">Book tickets</button>`;
        return `
          <div class="tastings-page${avail.sold ? '' : ' tastings-page--mobile-bar'}">
            <div class="tastings-container">
              <div class="tastings-crumbs">
                <button type="button" data-go="/">Whisky Tastings</button><span>›</span>
                <button type="button" data-go="/events">Events</button><span>›</span>
                <span class="current">${escapeHtml(event.title)}</span>
              </div>
              <div class="tastings-event-hero">
                <div class="tastings-cover tastings-cover--dark tastings-event-hero-frame">
                  ${coverImg(event, event.title, true)}
                  <div class="tastings-date-chip"><b>${escapeHtml(bits.day)}</b><span>${escapeHtml(bits.monthShort)}</span></div>
                  <div class="tastings-art-stamp">${escapeHtml(event.kicker || showcase.kicker || 'Guided tasting')}</div>
                  <div class="tastings-event-hero-copy">
                    <p class="tastings-eyebrow">${escapeHtml(formatWhen(event.starts_at))}</p>
                    <h1>${escapeHtml(event.title)}</h1>
                    <div class="tastings-tag-row">${tagsHtml}${extras}</div>
                  </div>
                </div>
                <aside class="tastings-booking-box">
                  <div class="event-date">${escapeHtml(bits.weekdayShort)} ${escapeHtml(bits.day)} ${escapeHtml(bits.month)}</div>
                  <div class="tastings-detail-list">
                    <div><b>Time</b><span>${escapeHtml(whenRange(event))}</span></div>
                    <div><b>Venue</b><span>${escapeHtml(event.venue_address || event.venue_name || '')}</span></div>
                    <div><b>Includes</b><span>${escapeHtml(showcase.includes || included.map((row) => row.name).join(', ') || 'Hosted tasting')}</span></div>
                    <div><b>Age</b><span>${escapeHtml(showcase.age || '18+ · ID may be requested')}</span></div>
                  </div>
                  <div class="tastings-line"></div>
                  ${avail.notice
                    ? `<div class="tastings-availability">${escapeHtml(avail.label)}</div><p class="tastings-smallprint">${escapeHtml(avail.remaining)}</p>`
                    : `<div class="tastings-price">${escapeHtml(money(fromPrice(event), state.store?.currency))} <small>per person</small></div>
                  <div class="tastings-availability${avail.sold ? ' tastings-availability--sold' : ''}">${escapeHtml(avail.label)}</div>
                  <div class="tastings-line"></div>
                  ${avail.sold
                    ? '<button class="tastings-btn tastings-btn--primary tastings-desktop-cta" style="width:100%" type="button" disabled>Sold out</button>'
                    : shopify
                      ? shopifyBookLink(event, 'Book tickets', 'tastings-btn tastings-btn--primary tastings-desktop-cta').replace('class="', 'style="width:100%" class="')
                      : `<button class="tastings-btn tastings-btn--primary tastings-desktop-cta" style="width:100%" type="button" data-go="/checkout/${escapeHtml(event.slug)}">Book tickets</button>`}
                  ${shopify
                    ? accountLink('Already booked?', 'tastings-btn tastings-btn--ghost tastings-desktop-cta').replace('class="', 'style="width:100%;margin-top:9px" class="')
                    : '<button class="tastings-btn tastings-btn--ghost tastings-desktop-cta" style="width:100%;margin-top:9px" type="button" data-go="/bookings">Already booked?</button>'}`}
                </aside>
              </div>
            </div>
            ${tastingList || timeline ? `
            <div class="tastings-section tastings-section--white">
              <div class="tastings-container tastings-story-layout">
                <div class="tastings-prose">
                  <p class="tastings-eyebrow">What you'll taste</p>
                  <h2>${escapeHtml(showcase.story_heading || event.title)}</h2>
                  ${descriptionBlocks(event.description)}
                  <div class="tastings-list">${tastingList}</div>
                </div>
                <div>
                  <p class="tastings-eyebrow">Your evening</p>
                  <h2 style="font-size:38px;margin-bottom:20px">How it flows</h2>
                  <div class="tastings-timeline">${timeline}</div>
                </div>
              </div>
            </div>` : (shopify || avail.notice) && event.description ? `
            <div class="tastings-section tastings-section--white">
              <div class="tastings-container">
                <p class="tastings-eyebrow">The tasting</p>
                <h2>${escapeHtml(event.title)}</h2>
                <div class="tastings-prose">${descriptionBlocks(event.description)}</div>
              </div>
            </div>` : ''}
            ${good ? `<div class="tastings-section"><div class="tastings-container"><div class="tastings-section-head"><div><p class="tastings-eyebrow">Good to know</p><h2>Before you arrive</h2></div></div><div class="tastings-good-grid">${good}</div></div></div>` : ''}
            ${bookAction ? mobileBar({
              kicker: 'From',
              amount: money(fromPrice(event), state.store?.currency),
              meta: `${avail.remaining} · ${bits.weekdayShort} ${bits.day} ${bits.monthShort}`,
              action: bookAction,
            }) : ''}
          </div>`;
      }

      function checkoutTotals(event) {
        const quantities = state.checkout?.quantities || {};
        const extras = state.checkout?.extras || {};
        let tickets = 0;
        let addOns = 0;
        let total = 0;
        const lines = [];
        for (const type of event.ticket_types || []) {
          const qty = Number(quantities[type.id] || 0);
          tickets += qty;
          total += qty * Number(type.price || 0);
          if (qty) lines.push(`${qty} × ${type.name}`);
        }
        for (const product of (event.products || []).filter((row) => row.role === 'upsell')) {
          const qty = Number(extras[product.id] || 0);
          addOns += qty;
          total += qty * Number(product.price || 0);
          if (qty) lines.push(`${qty} × ${product.name}`);
        }
        return { tickets, addOns, lines, total: Math.round(total * 100) / 100 };
      }

      function mobileBar({ kicker, amount, meta, action }) {
        return `
          <div class="tastings-mobile-bar" role="region" aria-label="Booking">
            <div class="tastings-mobile-bar-copy" aria-live="polite">
              ${kicker ? `<p class="tastings-eyebrow">${escapeHtml(kicker)}</p>` : ''}
              <strong>${amount}</strong>
              ${meta ? `<span>${escapeHtml(meta)}</span>` : ''}
            </div>
            ${action}
          </div>`;
      }

      function ensureCheckout(event) {
        if (state.checkout?.slug === event.slug) return;
        const quantities = {};
        const reserveOnly = isEcaRsvp(event);
        (event.ticket_types || []).forEach((type, index) => {
          const min = Number(type.min_per_booking || 1);
          const available = Number(type.available || 0);
          quantities[type.id] = !reserveOnly && index === 0 ? Math.min(Math.max(min, Math.min(2, available)), available) : 0;
        });
        state.checkout = {
          slug: event.slug,
          quantities,
          extras: {},
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          notes: '',
          marketing: false,
          guests: {},
          result: null,
          error: '',
          step: 1,
        };
      }

      function checkoutView(event) {
        if (!event) return eventView(null);
        ensureCheckout(event);
        const checkout = state.checkout;
        if (checkout.result) return confirmationView(event, checkout.result);
        const settings = { ...(state.store?.settings || {}), ...(event.settings || {}) };
        const totals = checkoutTotals(event);
        const named = settings.require_named_tickets;
        const guestFields = [];
        if (named) {
          let index = 0;
          for (const type of event.ticket_types || []) {
            const qty = Number(checkout.quantities[type.id] || 0);
            for (let i = 0; i < qty; i += 1) {
              const key = `${type.id}:${i}`;
              guestFields.push(`<div class="tastings-form-group tastings-form-group--full"><label>Guest ${index + 1} name</label><input class="tastings-field" data-guest="${escapeHtml(key)}" value="${escapeHtml(checkout.guests[key] || '')}" required></div>`);
              index += 1;
            }
          }
        }
        const upsells = (event.products || []).filter((row) => row.role === 'upsell');
        const included = (event.products || []).filter((row) => row.role === 'included');
        const reserveOnly = isEcaRsvp(event);
        const ctaLabel = reserveOnly ? 'Reserve your place' : shopify ? 'Continue to payment' : event.booking_mode === 'request_then_pay' ? 'Submit request' : 'Continue to payment';
        const basketMeta = totals.lines.length ? totals.lines.join(' · ') : 'Add tickets to continue';
        const guestLabel = totals.tickets === 1 ? '1 ticket' : `${totals.tickets} tickets`;
        const addOnLabel = totals.addOns ? ` · ${totals.addOns} add-on${totals.addOns === 1 ? '' : 's'}` : '';
        return `
          <div class="tastings-page tastings-page--mobile-bar">
            <div class="tastings-container tastings-page-inner">
              <div class="tastings-crumbs">
                <button type="button" data-go="/event/${escapeHtml(event.date_path || event.slug)}">${escapeHtml(event.title)}</button><span>›</span><span class="current">Book tickets</span>
              </div>
              <div class="tastings-page-head" style="padding-top:30px;padding-bottom:4px"><h1 style="font-size:54px">Book your seats</h1></div>
              <div class="tastings-stepper">
                <div class="tastings-step is-active"><span>1</span>Tickets</div>
                <div class="tastings-step${reserveOnly || shopify || checkout.step >= 2 ? ' is-active' : ''}"><span>2</span>${reserveOnly || !shopify ? 'Your details' : 'Payment'}</div>
                <div class="tastings-step"><span>3</span>Confirmation</div>
              </div>
              ${checkout.error ? `<div class="tastings-error">${escapeHtml(checkout.error)}</div>` : ''}
              <form class="tastings-checkout-grid" id="tastings-checkout-form" data-checkout>
                <div class="tastings-panel">
                  <h2>Tickets</h2>
                  ${(event.ticket_types || []).map((type) => `
                    <div class="tastings-ticket-choice">
                      <div>
                        <strong style="color:var(--navy)">${escapeHtml(type.name)}</strong>
                        <div style="color:#757684;font-size:13px;margin-top:4px">${escapeHtml(type.description || '')}${type.description ? ' · ' : ''}${escapeHtml(money(type.price, state.store?.currency))}${type.sold_out ? ' · Sold out' : ''}</div>
                      </div>
                      ${type.sold_out ? '<div class="tastings-status">Sold out</div>' : `<div class="tastings-qty">
                        <button type="button" data-qty="${escapeHtml(type.id)}" data-delta="-1">−</button>
                        <span>${escapeHtml(checkout.quantities[type.id] || 0)}</span>
                        <button type="button" data-qty="${escapeHtml(type.id)}" data-delta="1">+</button>
                      </div>`}
                    </div>`).join('')}
                  ${upsells.length ? `<h2 style="font-size:28px;margin-top:28px">Add-ons</h2>${upsells.map((product) => `
                    <div class="tastings-extra-choice">
                      <div>
                        <strong style="color:var(--navy)">${escapeHtml(product.name)}</strong>
                        <div style="color:#757684;font-size:13px;margin-top:4px">${escapeHtml(money(product.price, state.store?.currency))}</div>
                      </div>
                      <div class="tastings-qty">
                        <button type="button" data-extra="${escapeHtml(product.id)}" data-delta="-1">−</button>
                        <span>${escapeHtml(checkout.extras[product.id] || 0)}</span>
                        <button type="button" data-extra="${escapeHtml(product.id)}" data-delta="1">+</button>
                      </div>
                    </div>`).join('')}` : ''}
                  ${included.length ? `<p class="tastings-smallprint" style="margin-top:16px">Included: ${escapeHtml(included.map((row) => row.name).join(', '))}</p>` : ''}
                  ${shopify && !reserveOnly ? '' : `<h2 style="font-size:28px;margin-top:28px">Your details</h2>
                  <div class="tastings-form-grid">
                    <div class="tastings-form-group"><label>First name</label><input class="tastings-field" name="firstName" value="${escapeHtml(checkout.firstName)}" required></div>
                    <div class="tastings-form-group"><label>Last name</label><input class="tastings-field" name="lastName" value="${escapeHtml(checkout.lastName)}" required></div>
                    <div class="tastings-form-group tastings-form-group--full"><label>Email</label><input class="tastings-field" type="email" name="email" value="${escapeHtml(checkout.email)}" required></div>
                    <div class="tastings-form-group tastings-form-group--full"><label>Mobile number${settings.require_phone ? '' : ' (optional)'}</label><input class="tastings-field" name="phone" value="${escapeHtml(checkout.phone)}" ${settings.require_phone ? 'required' : ''}></div>
                    ${guestFields.join('')}
                    <div class="tastings-form-group tastings-form-group--full"><label>Notes</label><input class="tastings-field" name="notes" value="${escapeHtml(checkout.notes)}"></div>
                  </div>`}
                </div>
                <aside class="tastings-panel tastings-panel--media">
                  ${coverMedia(event, 'tastings-summary-cover', event.title)}
                  <div class="tastings-panel-body">
                    <p class="tastings-eyebrow">Order summary</p>
                    <h2 style="font-size:28px">${escapeHtml(event.title)}</h2>
                    <div class="tastings-summary-row"><span>${escapeHtml(formatWhen(event.starts_at))}</span><span>${escapeHtml(event.venue_name || 'Soho')}</span></div>
                    <div class="tastings-summary-row"><span>${escapeHtml(totals.tickets)} × ticket</span><span>${escapeHtml(money(totals.total, state.store?.currency))}</span></div>
                    <div class="tastings-summary-total"><span>Total</span><span>${escapeHtml(money(totals.total, state.store?.currency))}</span></div>
                    <button class="tastings-btn tastings-btn--gold tastings-desktop-cta" style="width:100%;margin-top:22px" type="submit"${state.placing || totals.tickets < 1 ? ' disabled' : ''}>${escapeHtml(ctaLabel)}</button>
                    <p class="tastings-smallprint">${reserveOnly ? 'This tasting is free. Reserving a place holds your seat for the session you choose.' : shopify ? 'Choose your tickets, then pay securely on 3 Greek Street checkout.' : event.booking_mode === 'request_then_pay' ? 'No payment is taken until the tasting room confirms your request.' : 'Prices shown are VAT-inclusive. If payment is due, you will complete it on Order2Go — not through this site.'}</p>
                  </div>
                </aside>
              </form>
              ${mobileBar({
                kicker: totals.tickets ? `${guestLabel}${addOnLabel}` : 'Your basket',
                amount: money(totals.total, state.store?.currency),
                meta: basketMeta,
                action: `<button class="tastings-btn tastings-btn--gold" type="submit" form="tastings-checkout-form"${state.placing || totals.tickets < 1 ? ' disabled' : ''}>${escapeHtml(ctaLabel)}</button>`,
              })}
            </div>
          </div>`;
      }

      function confirmationView(event, placed) {
        const reserveOnly = placed.next === 'eca_confirmed' || isEcaRsvp(event);
        const requestOnly = !reserveOnly && (placed.next === 'request_received' || event.booking_mode === 'request_then_pay');
        return `
          <div class="tastings-page">
            <div class="tastings-container tastings-page-inner">
              <div class="tastings-stepper">
                <div class="tastings-step is-done"><span>1</span>Tickets</div>
                <div class="tastings-step is-done"><span>2</span>Your details</div>
                <div class="tastings-step is-active"><span>3</span>Confirmation</div>
              </div>
              <div class="tastings-panel tastings-panel--media">
                ${coverMedia(event, 'tastings-cover--dark tastings-success-cover', event.title)}
                <div class="tastings-success-box">
                <div class="tastings-success-icon">✓</div>
                <p class="tastings-eyebrow">${reserveOnly ? 'Place reserved' : requestOnly ? 'Request received' : 'Booking confirmed'}</p>
                <h2>${reserveOnly ? "You're booked in." : requestOnly ? 'We have your request.' : "You're on the guest list."}</h2>
                <p>${reserveOnly
                  ? escapeHtml(placed.message || 'Your place is reserved. Tell the team which session you booked when you arrive.')
                  : requestOnly
                  ? 'No card has been charged. The tasting room will confirm your seats, then email a pay link if payment is needed. Your hold keeps the tickets until then.'
                  : placed.next === 'confirmed' || placed.payment_status === 'paid'
                    ? 'Your tickets are ready. Bring the booking reference or QR to check-in.'
                    : 'Card payment happens on Order2Go. If that page did not open, use View my tickets and continue to payment.'}</p>
                ${reserveOnly ? '' : `<div class="tastings-order-ref">${escapeHtml(placed.booking_number)}</div>`}
                <div class="tastings-btn-row" style="justify-content:center">
                  ${reserveOnly ? '' : `<button class="tastings-btn tastings-btn--primary" type="button" data-go="/booking/${escapeHtml(placed.booking_id)}">View my tickets</button>`}
                  <button class="tastings-btn" type="button" data-go="/events">Browse more tastings</button>
                </div>
                </div>
              </div>
            </div>
          </div>`;
      }

      async function bookingsView() {
        const records = [];
        for (const id of readTokenIds()) {
          const token = readToken(id);
          if (!token) continue;
          try {
            const data = await api.getBooking(id, token);
            if (data) records.push(data);
          } catch {
            /* skip stale tokens */
          }
        }
        const initials = (records[0]?.booking?.customer_name || 'Guest').split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase();
        return `
          <div class="tastings-page">
            <div class="tastings-container">
              <div class="tastings-page-head">
                <p class="tastings-eyebrow">My Milroy's</p>
                <h1>Tickets &amp; bookings</h1>
                <p>Everything related to your tasting tickets lives here — upcoming events, ticket details and booking management.</p>
              </div>
              <div class="tastings-account-shell">
                <aside class="tastings-account-side">
                  <div class="tastings-side-user">
                    <div class="tastings-avatar">${escapeHtml(initials)}</div>
                    <strong>${escapeHtml(records[0]?.booking?.customer_name || 'Guest')}</strong>
                    <small>${escapeHtml(records[0]?.booking?.customer_email || 'Look up a booking below')}</small>
                  </div>
                </aside>
                <div class="tastings-account-main">
                  <h2>Upcoming bookings</h2>
                  <div class="sub">${records.length} stored on this device</div>
                  ${records.map((row) => {
                    const event = row.event || {};
                    const bits = event.starts_at ? dateBits(event.starts_at) : { monthShort: '', day: '' };
                    const when = event.starts_at ? formatWhen(event.starts_at) : 'Date to be confirmed';
                    return `
                      <article class="tastings-booking-card">
                        <div class="tastings-cover tastings-booking-media">
                          ${coverImg(event, event.name || event.title || '')}
                          <div class="tastings-mini-date"><span>${escapeHtml(bits.monthShort)}</span><b>${escapeHtml(bits.day)}</b></div>
                        </div>
                        <div>
                          <div class="tastings-ticket-status ${statusClass(row.booking.status)}">${escapeHtml(statusLabel(row.booking.status, row.booking.payment_status))}</div>
                          <h3>${escapeHtml(event.name || event.title || 'Tasting')}</h3>
                          <p>${escapeHtml(when)} · ${escapeHtml((row.tickets || []).length)} guest${(row.tickets || []).length === 1 ? '' : 's'}</p>
                          <div class="tastings-booking-ref">Booking ${escapeHtml(row.booking.booking_number)}</div>
                        </div>
                        <button class="tastings-btn tastings-btn--primary" type="button" data-go="/booking/${escapeHtml(row.booking.id)}">View tickets</button>
                      </article>`;
                  }).join('') || '<p>No bookings stored in this browser yet.</p>'}
                  ${typeof api.lookup === 'function' ? `
                  <form class="tastings-lookup" data-lookup>
                    <h3>Booked without this browser?</h3>
                    <p>Use your booking reference and the email address used at checkout. Access tokens from confirmation emails also open the ticket page directly.</p>
                    <div class="tastings-lookup-row">
                      <input class="tastings-field" name="reference" placeholder="Booking reference" required>
                      <input class="tastings-field" type="email" name="email" placeholder="Email address" required>
                      <button class="tastings-btn tastings-btn--primary" type="submit">Find booking</button>
                    </div>
                    <div data-lookup-error></div>
                  </form>` : `
                  <div class="tastings-lookup">
                    <h3>Booked on another device?</h3>
                    <p>Open the link in your confirmation email. That page holds your tickets and, when needed, the Order2Go payment screen.</p>
                  </div>`}
                </div>
              </div>
            </div>
          </div>`;
      }

      function ticketView(data, bookingId) {
        if (!data) {
          return `<div class="tastings-empty tastings-container"><h2>Booking not found</h2><p>This link may have expired, or the access token is missing.</p><button class="tastings-btn tastings-btn--primary" type="button" data-go="/bookings">Look up a booking</button></div>`;
        }
        const event = data.event || {};
        const tickets = data.tickets || [];
        const booking = data.booking;
        const currency = booking.currency || state.store?.currency || 'GBP';
        const paid = booking.payment_status === 'paid';
        const terminal = ['expired', 'declined', 'cancelled', 'refunded'].includes(booking.status);
        const awaitingRequest = booking.status === 'pending_confirmation';
        const showCodes = paid && tickets.some((ticket) => ['active', 'checked_in'].includes(ticket.status));
        const settings = event.settings || {};
        const canRefund = settings.allow_refunds && booking.status === 'confirmed' && paid && !booking.refund_request;
        const needsPay = !paid && !terminal && !awaitingRequest && Number(booking.total_amount) > 0;
        const due = money(booking.total_amount, currency);
        const holdCopy = booking.hold_expires_at
          ? `Your hold expires ${formatWhen(booking.hold_expires_at)}.`
          : 'Complete payment to keep these seats.';
        const heading = showCodes
          ? 'Your tickets are ready'
          : needsPay
            ? 'Payment needed'
            : awaitingRequest
              ? 'Request received'
              : 'Your booking';
        const lede = showCodes
          ? 'Show the code at check-in. These are your entry credentials for the tasting.'
          : needsPay
            ? `This booking is unpaid, so it is not a valid ticket yet. Pay ${due} on Order2Go to confirm your seats and receive check-in codes.`
            : awaitingRequest
              ? 'The tasting room still needs to confirm these seats. Check-in codes appear after confirmation and payment — nothing is charged yet.'
              : 'This booking is not valid for entry.';
        const alert = showCodes ? '' : `
          <div class="tastings-alert ${needsPay || terminal ? 'tastings-alert--warning' : 'tastings-alert--info'}" role="status">
            <div>
              <strong>${escapeHtml(needsPay ? 'Unpaid — not valid for entry' : awaitingRequest ? 'Waiting for confirmation' : statusLabel(booking.status, booking.payment_status))}</strong>
              <p>${escapeHtml(lede)}</p>
            </div>
            ${needsPay ? payLink(bookingId, `Pay ${due} now`) : ''}
          </div>`;
        const aside = showCodes
          ? `
                <aside class="tastings-qr-box">
                  <p class="tastings-eyebrow">Check-in code</p>
                  <h3>Booking QR</h3>
                  <div class="tastings-qr">${escapeHtml(tickets[0]?.ticket_code || booking.booking_number)}</div>
                  <p>One code can check in the whole party when tickets are active.</p>
                  <div class="tastings-manage-actions">
                    ${canRefund ? `<button class="tastings-btn tastings-btn--ghost" type="button" data-action="refund" data-booking="${escapeHtml(bookingId)}">Request a refund</button>` : ''}
                    ${booking.refund_request ? '<p class="tastings-smallprint">A refund request is with the tasting room. No money has been moved yet.</p>' : ''}
                  </div>
                </aside>`
          : `
                <aside class="tastings-pay-warning">
                  <p class="tastings-eyebrow">${escapeHtml(needsPay ? 'Action required' : awaitingRequest ? 'Not a ticket yet' : 'Booking unavailable')}</p>
                  <h3>${escapeHtml(needsPay ? 'Pay to get your tickets' : awaitingRequest ? 'Seats are on hold' : statusLabel(booking.status, booking.payment_status))}</h3>
                  <p>${escapeHtml(needsPay
                    ? `No QR or check-in code is shown until payment is complete. ${holdCopy}`
                    : awaitingRequest
                      ? 'Staff will confirm in POS. You will then get a pay link if a charge is due. This page will not show entry codes until then.'
                      : 'Check-in codes are not available for this booking.')}</p>
                  ${needsPay ? `<div class="tastings-pay-amount">${escapeHtml(due)} due</div>` : ''}
                  ${needsPay ? payLink(bookingId, 'Pay now on Order2Go') : ''}
                  <div class="tastings-manage-actions">
                    ${canRefund ? `<button class="tastings-btn tastings-btn--ghost" type="button" data-action="refund" data-booking="${escapeHtml(bookingId)}">Request a refund</button>` : ''}
                    ${booking.refund_request ? '<p class="tastings-smallprint">A refund request is with the tasting room. No money has been moved yet.</p>' : ''}
                  </div>
                </aside>`;
        return `
          <div class="tastings-page">
            <div class="tastings-container">
              <div class="tastings-crumbs"><button type="button" data-go="/bookings">Tickets &amp; bookings</button><span>›</span><span class="current">${escapeHtml(booking.booking_number)}</span></div>
              <div class="tastings-page-head" style="padding-bottom:24px">
                <p class="tastings-eyebrow">${escapeHtml(statusLabel(booking.status, booking.payment_status))}</p>
                <h1 style="font-size:54px">${escapeHtml(heading)}</h1>
                <p>${escapeHtml(lede)}</p>
              </div>
              ${alert}
              <div class="tastings-ticket-layout">
                <div class="tastings-ticket-card-main">
                  <div class="tastings-ticket-top">
                    ${coverMedia(event, 'tastings-cover--dark', event.name || event.title || '')}
                    <div class="tastings-ticket-top-copy">
                      <p class="tastings-eyebrow">${escapeHtml(event.starts_at ? formatWhen(event.starts_at) : '')}</p>
                      <h2>${escapeHtml(event.name || event.title || 'Tasting')}</h2>
                    </div>
                  </div>
                  <div class="tastings-ticket-body">
                    <div class="tastings-ticket-info-grid">
                      <div class="tastings-info-box"><small>Booking reference</small><strong>${escapeHtml(booking.booking_number)}</strong></div>
                      <div class="tastings-info-box"><small>Guests</small><strong>${escapeHtml(tickets.length)} ${tickets.length === 1 ? 'person' : 'people'}</strong></div>
                      <div class="tastings-info-box"><small>Venue</small><strong>${escapeHtml(event.venue_name || '')}</strong></div>
                    </div>
                    <h2 style="font-size:30px;margin-bottom:10px">${showCodes ? 'Guest tickets' : 'Guests on this booking'}</h2>
                    <div class="tastings-guest-table${showCodes ? '' : ' tastings-guest-table--pending'}">
                      ${tickets.map((ticket, index) => `
                        <div class="tastings-guest-row">
                          <div>
                            <div class="name">${escapeHtml(ticket.guest_name || `Guest ${index + 1}`)}</div>
                            <small>${index === 0 ? 'Lead booker' : 'Guest'}</small>
                          </div>
                          <span class="tastings-ticket-status ${statusClass(showCodes ? 'confirmed' : booking.status)}">${escapeHtml(showCodes ? 'Ready' : needsPay ? 'Awaiting payment' : awaitingRequest ? 'Requested' : statusLabel(booking.status, booking.payment_status))}</span>
                          ${showCodes ? `<span>${escapeHtml(ticket.ticket_code)}</span>` : ''}
                        </div>`).join('')}
                    </div>
                  </div>
                </div>
                ${aside}
              </div>
            </div>
          </div>`;
      }

      function mockPayView(bookingId, token) {
        return `
          <div class="tastings-page">
            <div class="tastings-container tastings-page-inner">
              <div class="tastings-panel tastings-panel--media">
                ${coverMedia(null, 'tastings-cover--dark tastings-success-cover', 'Milroy\'s tasting room')}
                <div class="tastings-success-box">
                  <p class="tastings-eyebrow">Mock checkout</p>
                  <h2>Pay for your tasting</h2>
                  <p>This stands in for Stripe or PossoPay. No card is charged.</p>
                  <div class="tastings-btn-row" style="justify-content:center">
                    <button class="tastings-btn tastings-btn--gold" type="button" data-action="mock-pay" data-booking="${escapeHtml(bookingId)}" data-token="${escapeHtml(token)}">Simulate successful payment</button>
                    <button class="tastings-btn" type="button" data-go="/pay/${escapeHtml(bookingId)}?token=${encodeURIComponent(token)}&payment=cancelled">Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          </div>`;
      }

      function payView(data, query) {
        const paymentQuery = query.get('payment');
        const paid = data?.booking?.payment_status === 'paid';
        const retryHref = data && !paid ? payHref(data.booking.id) : '';
        const retryTop = retryHref.startsWith('http') ? ' target="_top" rel="noopener"' : '';
        return `
          <div class="tastings-page">
            <div class="tastings-container tastings-page-inner">
              <div class="tastings-panel tastings-panel--media">
                ${coverMedia(data?.event, 'tastings-cover--dark tastings-success-cover', data?.event?.name || 'Milroy\'s tasting room')}
                <div class="tastings-success-box">
                  <div class="tastings-success-icon">${paid ? '✓' : paymentQuery === 'cancelled' ? '!' : '…'}</div>
                  <p class="tastings-eyebrow">${paid ? 'Payment confirmed' : paymentQuery === 'cancelled' ? 'Payment cancelled' : 'Checking payment'}</p>
                  <h2>${paid ? "You're on the guest list." : paymentQuery === 'cancelled' ? 'Payment was not completed.' : 'We are confirming your payment.'}</h2>
                  <p>${paid
                    ? 'Taking you to your tickets. This page re-fetched the booking — it does not trust the return URL alone.'
                    : paymentQuery === 'cancelled'
                      ? 'Your seats are still on hold if time remains. Complete payment to get check-in codes.'
                      : 'If you have just paid, this can take a few seconds. Ticket codes appear only after the booking shows as paid.'}</p>
                  ${data?.booking?.booking_number ? `<div class="tastings-order-ref">${escapeHtml(data.booking.booking_number)}</div>` : ''}
                  <div class="tastings-btn-row" style="justify-content:center">
                    ${paid && data ? `<button class="tastings-btn tastings-btn--primary" type="button" data-go="/booking/${escapeHtml(data.booking.id)}">View tickets</button>` : ''}
                    ${!paid && retryHref ? `<a class="tastings-btn tastings-btn--gold" href="${escapeHtml(retryHref)}"${retryTop}>${paymentQuery === 'cancelled' ? 'Try payment again' : 'Continue to payment'}</a>` : ''}
                  </div>
                </div>
              </div>
            </div>
          </div>`;
      }

      let payPollTimer = 0;

      function unavailableView() {
        return `<div class="tastings-empty tastings-container"><h2>Events unavailable</h2><p>The tasting room is not currently taking online bookings.</p></div>`;
      }

      function spotlightEvents() {
        return currentEvents()
          .filter((event) => !isDiaryNote(event))
          .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
      }

      function embedView() {
        const limit = Number(config.limit || 3);
        const offset = Math.max(0, Number(config.offset || 0));
        const source = offset > 0 ? spotlightEvents() : spotlightEvents().filter((event) => !isNotice(event));
        const list = source.slice(offset, offset + limit);
        if (!list.length) {
          return `<p class="tastings-section-sub">No tastings are on sale yet.</p>`;
        }
        return `<div class="tastings-event-grid">${list.map(card).join('')}</div>`;
      }

      async function render() {
        const route = parseRoute();
        if (payPollTimer) {
          window.clearTimeout(payPollTimer);
          payPollTimer = 0;
        }
        try {
          if (!state.store) await loadCatalog();
          if (!state.store?.found) {
            root.innerHTML = unavailableView();
            return;
          }
          if (config.embed) {
            root.innerHTML = embedView();
            return;
          }
          if (config.layout === 'next') {
            const featured = spotlightEvents()[0];
            root.innerHTML = featured ? featuredBlock(featured) : '';
            return;
          }
          const section = route.parts[0] || '';
          if (!section) {
            const returned = readReturnQuery();
            if (returned.complete) {
              root.innerHTML = `<div class="tastings-empty tastings-container"><h2>Looking for your booking</h2><p>Open the link from your confirmation email, or go back to tickets and bookings.</p><button class="tastings-btn tastings-btn--primary" type="button" data-go="/bookings">View my bookings</button></div>`;
              return;
            }
            root.innerHTML = landingView();
            return;
          }
          if (section === 'events') {
            root.innerHTML = eventsView();
            return;
          }
          if (section === 'event') {
            root.innerHTML = eventView(await eventBySlug(route.parts[1]));
            return;
          }
          if (section === 'checkout') {
            const tasting = await eventBySlug(route.parts[1]);
            if (shopify && tasting && !config.pathRouting) {
              goToPayment(purchaseHref(tasting, 'checkout'));
              return;
            }
            if (shopify && tasting) sessionStorage.setItem(CHECKOUT_EVENT_KEY, tastingPath(tasting));
            root.innerHTML = checkoutView(tasting);
            return;
          }
          if (section === 'bookings') {
            if (shopify) {
              goToPayment(accountUrl);
              return;
            }
            root.innerHTML = await bookingsView();
            return;
          }
          if (section === 'booking') {
            const id = route.parts[1];
            const token = route.query.get('token') || readToken(id);
            const data = token ? await api.getBooking(id, token) : null;
            if (data) saveToken(data.booking.id, token);
            root.innerHTML = ticketView(data, id);
            return;
          }
          if (section === 'mock-pay') {
            root.innerHTML = mockPayView(route.parts[1], route.query.get('token') || readToken(route.parts[1]) || '');
            return;
          }
          if (section === 'pay') {
            const id = route.parts[1];
            const token = route.query.get('token') || readToken(id);
            const data = token ? await api.getBooking(id, token) : null;
            const paid = data?.booking?.payment_status === 'paid';
            root.innerHTML = payView(data, route.query);
            if (paid && data?.booking?.id && route.query.get('payment') !== 'cancelled') {
              payPollTimer = window.setTimeout(() => go(`/booking/${data.booking.id}`), 1400);
              return;
            }
            if (data && !paid && route.query.get('payment') !== 'cancelled') {
              payPollTimer = window.setTimeout(() => {
                const stillPay = parseRoute();
                if (stillPay.parts[0] === 'pay' && stillPay.parts[1] === id) render();
              }, 2500);
            }
            return;
          }
          root.innerHTML = landingView();
        } catch (error) {
          const fallback = shopify && !config.pathRouting
            ? `<a class="tastings-btn tastings-btn--gold" href="${escapeHtml(withPreview(`${purchaseOrigin}/pages/tastings`, purchaseOrigin))}" target="_top" rel="noopener">See tastings on 3 Greek Street</a>`
            : '';
          root.innerHTML = `<div class="tastings-empty tastings-container"><h2>Something went wrong</h2><p>${escapeHtml(error.message)}</p>${fallback}</div>`;
        }
      }

      async function startPay(bookingId) {
        const token = readToken(bookingId);
        if (!token) throw new Error('Missing booking access token');
        if (typeof api.startPayment === 'function') {
          const urls = payUrls(bookingId, token);
          const pay = await api.startPayment({
            bookingId,
            accessToken: token,
            successUrl: urls.success,
            cancelUrl: urls.cancel,
          });
          if (!pay?.payment_url) throw new Error('Secure payment could not be started.');
          goToPayment(pay.payment_url);
          return;
        }
        if (config.mode === 'mock_server') {
          const origin = new URL(config.apiBase, window.location.href).origin;
          goToPayment(`${origin}/mock-checkout?booking_id=${encodeURIComponent(bookingId)}&token=${encodeURIComponent(token)}`);
          return;
        }
        (window.top || window).location.assign(`${hostedBase}/pay/${encodeURIComponent(bookingId)}?token=${encodeURIComponent(token)}`);
      }

      async function submitShopifyCart(event) {
        const checkout = state.checkout;
        const quantity = Object.values(checkout.quantities || {}).reduce((sum, qty) => sum + Number(qty || 0), 0);
        if (!event?.variant_id || quantity < 1) {
          checkout.error = 'Choose at least one ticket.';
          await render();
          return;
        }
        state.placing = true;
        checkout.error = '';
        await render();
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [
                {
                  id: Number(event.variant_id),
                  quantity,
                  properties: {
                    Tasting: event.title,
                    Date: formatWhen(event.starts_at),
                  },
                },
              ],
            }),
          });
          if (!response.ok) {
            const payload = await response.json().catch(() => null);
            throw new Error(payload?.description || payload?.message || 'Could not add tickets to the cart.');
          }
          goToPayment(withPreview('/checkout', location.origin));
        } catch (error) {
          checkout.error = error.message || 'Could not add tickets to the cart.';
          state.placing = false;
          await render();
        }
      }

      function rsvpRequestBody(event, checkout) {
        const meta = event.eca || {};
        const ticketTypes = {};
        for (const [id, qty] of Object.entries(checkout.quantities || {})) {
          const quantity = Number(qty || 0);
          if (quantity > 0) ticketTypes[String(id)] = quantity;
        }
        return {
          name: `${checkout.firstName || ''} ${checkout.lastName || ''}`.trim(),
          email: String(checkout.email || '').trim(),
          ticketTypes,
          eventTitle: meta.eventTitle,
          eventUuid: meta.eventUuid,
          userId: meta.userId,
          widgetuuid: meta.widgetUuid,
          calendarId: meta.calendarId,
          timezoneStart: meta.timezoneStart,
          timezoneEnd: meta.timezoneEnd,
          currency: meta.currency,
          repeatId: meta.repeatId ?? null,
          longDescription: meta.longDescription || '',
          location: meta.location || null,
          utcEndTime: meta.utcEndTime,
          utcStartTime: meta.utcStartTime,
          timezone: meta.timezone,
          couponCode: null,
          timezoneAbbreviation: meta.timezoneAbbreviation,
        };
      }

      async function submitEcaRsvp(event) {
        const checkout = state.checkout;
        const body = rsvpRequestBody(event, checkout);
        if (!body.name || !body.email) {
          checkout.error = 'Add your name and email so we can hold the place.';
          await render();
          return;
        }
        if (!Object.keys(body.ticketTypes).length) {
          checkout.error = 'Choose a session.';
          await render();
          return;
        }
        state.placing = true;
        checkout.error = '';
        await render();
        try {
          const response = await fetch('https://api.eventcalendarapp.com/rsvp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const payload = await response.json().catch(() => null);
          if (!response.ok) {
            const message = payload?.message || payload?.error || 'Could not reserve that place.';
            if (message === 'Sold Out') throw new Error('That session has just sold out.');
            if (message === 'Quantity too high') {
              throw new Error(`Only ${payload?.numberOfTicketsAvailable ?? 'a few'} places are left for that session.`);
            }
            if (message === 'Sales Ended') throw new Error('Reservations for this tasting have closed.');
            throw new Error(message === 'Validation error' ? 'Check your name, email, and session, then try again.' : message);
          }
          checkout.result = {
            next: 'eca_confirmed',
            message: payload?.rsvp?.rsvpConfirmationPageMessage
              ? String(payload.rsvp.rsvpConfirmationPageMessage).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
              : event.eca?.confirmationMessage || 'Your place is reserved. Tell the team which session you booked when you arrive.',
          };
          checkout.step = 3;
        } catch (error) {
          checkout.error = error.message || 'Could not reserve that place.';
        } finally {
          state.placing = false;
          await render();
        }
      }

      async function submitCheckout(event) {
        if (state.placing) return;
        if (isEcaRsvp(event)) {
          await submitEcaRsvp(event);
          return;
        }
        if (shopify) {
          await submitShopifyCart(event);
          return;
        }
        const checkout = state.checkout;
        const tickets = Object.entries(checkout.quantities)
          .filter(([, qty]) => Number(qty) > 0)
          .map(([ticket_type_id, quantity]) => ({ ticket_type_id, quantity: Number(quantity) }));
        if (!tickets.length) {
          checkout.error = 'Choose at least one ticket.';
          await render();
          return;
        }
        const guests = [];
        for (const type of event.ticket_types || []) {
          const qty = Number(checkout.quantities[type.id] || 0);
          for (let i = 0; i < qty; i += 1) {
            guests.push({
              ticket_type_id: type.id,
              name: String(checkout.guests[`${type.id}:${i}`] || '').trim(),
            });
          }
        }
        const products = Object.entries(checkout.extras)
          .filter(([, qty]) => Number(qty) > 0)
          .map(([product_id, quantity]) => ({ product_id, quantity: Number(quantity) }));
        state.placing = true;
        checkout.error = '';
        let navigatedAway = false;
        await render();
        const settings = { ...(state.store?.settings || {}), ...(event.settings || {}) };
        const payload = {
          event_slug: event.slug,
          customer_name: `${checkout.firstName} ${checkout.lastName}`.trim(),
          customer_email: checkout.email,
          customer_phone: checkout.phone,
          marketing_consent: checkout.marketing,
          notes: checkout.notes,
          tickets,
        };
        const returnUrl = buildReturnUrl();
        if (returnUrl) payload.return_url = returnUrl;
        if (products.length) payload.products = products;
        if (settings.require_named_tickets) payload.guests = guests;
        try {
          const placed = flattenPlaced(await api.placeBooking(payload));
          saveToken(placed.booking_id, placed.access_token);
          if (placed.next === 'pay' && placed.pay_url) {
            navigatedAway = true;
            goToPayment(placed.pay_url);
            return;
          }
          checkout.result = placed;
          checkout.step = 3;
        } catch (error) {
          checkout.error = error.message || 'Could not place booking';
          try {
            const fresh = decorateEvent(await api.getEvent(event.slug));
            if (fresh) state.details.set(event.slug, fresh);
          } catch {
            /* keep stale catalog */
          }
        } finally {
          state.placing = false;
          if (!navigatedAway) await render();
        }
      }

      root.addEventListener('click', async (event) => {
        const goBtn = event.target.closest('[data-go]');
        if (goBtn) {
          event.preventDefault();
          go(goBtn.getAttribute('data-go'));
          return;
        }
        const pick = event.target.closest('[data-pick]');
        if (pick) {
          event.stopPropagation();
          const kind = pick.getAttribute('data-pick');
          const value = pick.getAttribute('data-value') || '';
          if (kind === 'tag') state.filter = value || 'All tastings';
          if (kind === 'month') state.month = value;
          if (kind === 'sort') state.sort = value || 'soonest';
          state.menu = '';
          await render();
          return;
        }
        const menuBtn = event.target.closest('[data-menu]');
        if (menuBtn) {
          event.stopPropagation();
          const menu = menuBtn.getAttribute('data-menu');
          state.menu = state.menu === menu ? '' : menu;
          await render();
          return;
        }
        if (event.target.closest('[data-clear-filters]')) {
          event.stopPropagation();
          state.filter = 'All tastings';
          state.month = '';
          state.sort = 'soonest';
          state.menu = '';
          await render();
          return;
        }
        if (state.menu && !event.target.closest('.tastings-filter-item')) {
          state.menu = '';
          if (!event.target.closest('a')) await render();
          return;
        }
        const qtyBtn = event.target.closest('[data-qty], [data-extra]');
        if (qtyBtn && state.checkout) {
          const ticketId = qtyBtn.getAttribute('data-qty');
          const extraId = qtyBtn.getAttribute('data-extra');
          const delta = Number(qtyBtn.getAttribute('data-delta') || 0);
          const route = parseRoute();
          const tasting = await eventBySlug(route.parts[1]);
          if (ticketId) {
            const type = (tasting.ticket_types || []).find((row) => row.id === ticketId);
            const next = Number(state.checkout.quantities[ticketId] || 0) + delta;
            state.checkout.quantities[ticketId] = Math.max(0, Math.min(Number(type?.available || 0), Number(type?.max_per_booking || 8), next));
          }
          if (extraId) {
            const product = (tasting.products || []).find((row) => row.id === extraId);
            const next = Number(state.checkout.extras[extraId] || 0) + delta;
            state.checkout.extras[extraId] = Math.max(0, Math.min(Number(product?.max_per_booking || 1), next));
          }
          await render();
          return;
        }
        const actionBtn = event.target.closest('[data-action]');
        if (!actionBtn) return;
        const action = actionBtn.getAttribute('data-action');
        const bookingId = actionBtn.getAttribute('data-booking');
        try {
          if (action === 'pay') await startPay(bookingId);
          if (action === 'mock-pay') {
            await api.activatePayment(bookingId, actionBtn.getAttribute('data-token'));
            go(`/pay/${bookingId}?token=${encodeURIComponent(actionBtn.getAttribute('data-token'))}`);
          }
          if (action === 'refund') {
            const token = readToken(bookingId);
            await api.requestRefund(bookingId, token, 'Requested from the tasting tickets page');
            await render();
          }
        } catch (error) {
          state.modal = error.message;
          await render();
        }
      });

      root.addEventListener('input', (event) => {
        if (!state.checkout) return;
        const field = event.target;
        if (field.name && field.name in state.checkout) state.checkout[field.name] = field.type === 'checkbox' ? field.checked : field.value;
        if (field.hasAttribute('data-guest')) state.checkout.guests[field.getAttribute('data-guest')] = field.value;
      });

      document.addEventListener('click', async (event) => {
        if (!state.menu || root.contains(event.target)) return;
        state.menu = '';
        await render();
      });

      root.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (event.target.matches('[data-checkout]')) {
          const tasting = await eventBySlug(parseRoute().parts[1]);
          await submitCheckout(tasting);
        }
        if (event.target.matches('[data-lookup]')) {
          if (typeof api.lookup !== 'function') return;
          const form = new FormData(event.target);
          try {
            const found = await api.lookup(String(form.get('reference') || ''), String(form.get('email') || ''));
            if (!found?.booking?.id) throw new Error('No booking matched that reference and email.');
            if (found.access_token) saveToken(found.booking.id, found.access_token);
            go(`/booking/${found.booking.id}`);
          } catch (error) {
            const holder = event.target.querySelector('[data-lookup-error]');
            if (holder) holder.innerHTML = `<div class="tastings-error">${escapeHtml(error.message)}</div>`;
          }
        }
      });

      if (config.embed) {
        embedPageUrl = config.pageUrl || '/pages/tastings';
        await render();
        return;
      }

      window.addEventListener('hashchange', () => {
        render();
      });
      window.addEventListener('popstate', () => {
        if (config.pathRouting) render();
      });
      window.addEventListener('tastings:route', () => {
        render();
      });

      const returned = readReturnQuery();
      if (returned.token && returned.bookingId) saveToken(returned.bookingId, returned.token);
      const storedIds = readTokenIds();
      const returnedId = returned.bookingId || (returned.complete ? storedIds[storedIds.length - 1] : '');
      const hashPath = (location.hash || '').replace(/^#/, '');
      const alreadyRouted = hashPath.startsWith('/pay/') || hashPath.startsWith('/booking/') || hashPath.startsWith('/mock-pay/');
      if (returnedId && !alreadyRouted) {
        const token = returned.token || readToken(returnedId) || '';
        const params = new URLSearchParams();
        if (token) params.set('token', token);
        if (returned.payment) params.set('payment', returned.payment);
        const qs = params.toString();
        go(`/pay/${returnedId}${qs ? `?${qs}` : ''}`);
        return;
      }

      await render();
    },
  };
})();
